/**
 * Admin inbox (Phase 3b).
 *
 * Real data from /api/submissions. List view only — the Sheet view + right-click
 * week menu come back in Phase 6.
 *
 * In scope for Phase 3b:
 *  - List of submissions, weekly grouping (computed client-side from submitted_at)
 *  - Filter chips backed by real counts (all/new/shortlisted/voicenotes/starred/archived)
 *  - Search across submitter name + snippet
 *  - Detail panel: full text, status pills, star, activity log
 *  - Unread state per-admin via localStorage
 *
 * Out of Phase 3b scope (intentionally stubbed with "coming soon" treatments):
 *  - Comments thread (Phase 5)
 *  - Reply composer (Phase 5)
 *  - Sheet view + week right-click menu (Phase 6)
 *  - Settings / Forms / Activity nav tabs
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { API_BASE, useAuth, type Admin } from '../lib/auth';

// ---------------- Types ----------------

type Status = 'new' | 'shortlisted' | 'used' | 'archived';
type Category =
  | 'relationships'
  | 'family'
  | 'money'
  | 'work'
  | 'friendship'
  | 'other';

const CATEGORY_VALUES: Category[] = [
  'relationships',
  'family',
  'money',
  'work',
  'friendship',
  'other',
];

type Submission = {
  id: string;
  form_id: string;
  submitter_name: string;
  submitter_email: string | null;
  submitted_at: string;
  status: Status;
  starred: boolean;
  category: Category | null;
  text_snippet: string;
  word_count: number;
  is_voicenote: boolean;
  voicenote_duration_seconds: number | null;
  transcription_status: 'not_required' | 'pending' | 'processing' | 'completed' | 'failed' | null;
  transcription_error: string | null;
  comment_count: number;
};

type SubmissionFull = Omit<Submission, 'text_snippet'> & {
  text_content: string | null;
  transcript: string | null;
};

type ActivityEntry = {
  id: string;
  admin_id: string | null;
  admin_email: string | null;
  admin_name: string | null;
  action: string;
  details: unknown;
  created_at: string;
};

type CommentEntry = {
  id: string;
  submission_id: string;
  admin_id: string;
  admin_email: string | null;
  admin_name: string | null;
  body: string;
  created_at: string;
  updated_at: string;
};

type ReplyEntry = {
  id: string;
  submission_id: string;
  direction: 'out' | 'in';
  from_admin_id: string | null;
  admin_email: string | null;
  admin_name: string | null;
  body: string;
  sent_at: string;
};

type Counts = {
  all: number;
  new: number;
  shortlisted: number;
  used: number;
  archived: number;
  starred: number;
  voicenotes: number;
  transcript_failed: number;
};

type FilterKey =
  | 'all'
  | 'new'
  | 'shortlisted'
  | 'voicenotes'
  | 'transcript_failed'
  | 'starred'
  | 'archived';

const STATUS_VALUES: Status[] = ['new', 'shortlisted', 'used', 'archived'];

// ---------------- Component ----------------

export default function AdminInbox() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const formId = params.id ?? null;
  const adminInitials = adminInitialsFrom(admin?.name, admin?.email);

  // Data
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [counts, setCounts] = useState<Counts>({
    all: 0,
    new: 0,
    shortlisted: 0,
    used: 0,
    archived: 0,
    starred: 0,
    voicenotes: 0,
    transcript_failed: 0,
  });
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');

  // UI state
  const [activeFilter, setActiveFilter] = useState<FilterKey>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedBulkIds, setSelectedBulkIds] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'list' | 'sheet'>('list');
  const [readIds, setReadIds] = useState<Set<string>>(() => loadReadIds(admin?.id));

  // Detail
  const [detail, setDetail] = useState<SubmissionFull | null>(null);
  const [detailActivity, setDetailActivity] = useState<ActivityEntry[]>([]);
  const [detailComments, setDetailComments] = useState<CommentEntry[]>([]);
  const [detailReplies, setDetailReplies] = useState<ReplyEntry[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [patching, setPatching] = useState(false);

  // ---- Fetch list ----
  const fetchList = useCallback(async () => {
    try {
      const qs = formId ? `?form_id=${encodeURIComponent(formId)}` : '';
      const res = await fetch(`${API_BASE}/api/submissions${qs}`, {
        credentials: 'include',
      });
      if (res.status === 401) {
        navigate('/login', { replace: true });
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as {
        ok: boolean;
        submissions: Submission[];
        counts: Counts;
      };
      setSubmissions(data.submissions);
      setCounts(data.counts);
      setListError('');
      // Auto-select first item only when the user hasn't picked one yet.
      // Use the functional setter so we read fresh state instead of the closure.
      setSelectedId((prev) => prev ?? data.submissions[0]?.id ?? null);
    } catch (err) {
      setListError(
        "We couldn't load the inbox. Refresh the page in a moment."
      );
      console.error('fetch_list_failed', err);
    } finally {
      setListLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, formId]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Fetches a single submission's full detail + activity log. Reused by the
  // selection effect below and by DetailPanel for transcript polling.
  const fetchDetail = useCallback(
    async (id: string, opts?: { silent?: boolean }) => {
      if (!opts?.silent) setDetailLoading(true);
      setDetailError('');
      try {
        const res = await fetch(`${API_BASE}/api/submissions/${id}`, {
          credentials: 'include',
        });
        if (res.status === 401) {
          navigate('/login', { replace: true });
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as {
          ok: boolean;
          submission: SubmissionFull;
          comments: CommentEntry[];
          replies: ReplyEntry[];
          activity: ActivityEntry[];
        };
        setDetail(data.submission);
        setDetailComments(data.comments ?? []);
        setDetailReplies(data.replies ?? []);
        setDetailActivity(data.activity);
      } catch (err) {
        console.error('fetch_detail_failed', err);
        if (!opts?.silent) {
          setDetailError("We couldn't load this dilemma. Try selecting it again.");
        }
      } finally {
        if (!opts?.silent) setDetailLoading(false);
      }
    },
    [navigate]
  );

  // ---- Fetch detail when selection changes ----
  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setDetailActivity([]);
      setDetailComments([]);
      setDetailReplies([]);
      return;
    }
    fetchDetail(selectedId);
  }, [selectedId, fetchDetail]);

  // Keep voicenote transcription status moving without forcing admins to reload.
  useEffect(() => {
    if (!selectedId || !detail?.is_voicenote) return;
    if (detail.transcription_status !== 'pending' && detail.transcription_status !== 'processing') {
      return;
    }

    const timer = window.setInterval(() => {
      fetchDetail(selectedId, { silent: true });
      fetchList();
    }, 5000);

    return () => window.clearInterval(timer);
  }, [
    selectedId,
    detail?.is_voicenote,
    detail?.transcription_status,
    fetchDetail,
    fetchList,
  ]);

  // ---- PATCH helpers ----
  async function patchSubmission(patch: { status?: Status; starred?: boolean }) {
    if (!selectedId || patching) return;
    setPatching(true);

    // Optimistic update on the row in the list + the detail panel.
    const prevList = submissions;
    const prevDetail = detail;
    setSubmissions((list) =>
      list.map((s) => (s.id === selectedId ? { ...s, ...patch } : s))
    );
    setDetail((d) => (d ? { ...d, ...patch } : d));

    try {
      const res = await fetch(`${API_BASE}/api/submissions/${selectedId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (res.status === 401) {
        navigate('/login', { replace: true });
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { ok: boolean; submission: Submission };
      // Replace with server truth (in case server normalised anything).
      setSubmissions((list) =>
        list.map((s) => (s.id === selectedId ? data.submission : s))
      );
      // Refresh counts + activity by refetching list and detail.
      await fetchList();
      // Refetch detail so the new activity row appears. Silent — keep the panel content.
      fetchDetail(selectedId, { silent: true });
    } catch (err) {
      // Roll back optimistic update.
      console.error('patch_failed', err);
      setSubmissions(prevList);
      setDetail(prevDetail);
    } finally {
      setPatching(false);
    }
  }

  async function patchDetail(patch: { category?: Category; transcript?: string }) {
    if (!selectedId || patching) return;
    setPatching(true);
    try {
      const res = await fetch(`${API_BASE}/api/submissions/${selectedId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (res.status === 401) {
        navigate('/login', { replace: true });
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      if (patch.category) {
        setDetail((d) => (d ? { ...d, category: patch.category ?? d.category } : d));
        setSubmissions((list) =>
          list.map((s) => (s.id === selectedId ? { ...s, category: patch.category ?? s.category } : s))
        );
      }
      if (patch.transcript !== undefined) {
        setDetail((d) =>
          d
            ? {
                ...d,
                transcript: patch.transcript ?? d.transcript,
                transcription_status: 'completed',
                transcription_error: null,
              }
            : d
        );
      }
      fetchDetail(selectedId, { silent: true });
      fetchList();
    } catch (err) {
      console.error('detail_patch_failed', err);
    } finally {
      setPatching(false);
    }
  }

  async function addComment(body: string) {
    if (!selectedId) return;
    const res = await fetch(`${API_BASE}/api/submissions/${selectedId}/comments`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body }),
    });
    if (res.status === 401) {
      navigate('/login', { replace: true });
      return;
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = (await res.json()) as { ok: boolean; comment: CommentEntry | null };
    if (data.comment) {
      setDetailComments((list) => [...list, data.comment as CommentEntry]);
      setSubmissions((list) =>
        list.map((s) =>
          s.id === selectedId ? { ...s, comment_count: s.comment_count + 1 } : s
        )
      );
      fetchDetail(selectedId, { silent: true });
    }
  }

  async function retryTranscription() {
    if (!selectedId || !detail?.is_voicenote) return;
    const res = await fetch(
      `${API_BASE}/api/submissions/${selectedId}/transcription/retry`,
      {
        method: 'POST',
        credentials: 'include',
      }
    );
    if (res.status === 401) {
      navigate('/login', { replace: true });
      return;
    }
    if (!res.ok && res.status !== 409) throw new Error(`HTTP ${res.status}`);
    setDetail((d) =>
      d
        ? {
            ...d,
            transcription_status: 'pending',
            transcription_error: null,
          }
        : d
    );
    fetchDetail(selectedId, { silent: true });
    fetchList();
  }

  async function sendReply(body: string) {
    if (!selectedId) return;
    const res = await fetch(`${API_BASE}/api/submissions/${selectedId}/replies`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body }),
    });
    if (res.status === 401) {
      navigate('/login', { replace: true });
      return;
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = (await res.json()) as { ok: boolean; reply: ReplyEntry | null };
    if (data.reply) {
      setDetailReplies((list) => [...list, data.reply as ReplyEntry]);
      fetchDetail(selectedId, { silent: true });
    }
  }

  // ---- Read state ----
  function markRead(id: string) {
    if (!admin?.id) return;
    setReadIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      saveReadIds(admin.id, next);
      return next;
    });
  }

  function selectSubmission(id: string) {
    setSelectedId(id);
    markRead(id);
  }

  function toggleBulk(id: string) {
    setSelectedBulkIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function bulkAction(action: Status | 'delete') {
    const ids = Array.from(selectedBulkIds);
    if (ids.length === 0) return;
    if (action === 'delete' && !window.confirm(`Permanently delete ${ids.length} selected dilemmas?`)) return;
    try {
      const res = await fetch(`${API_BASE}/api/submissions/bulk`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, action }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSelectedBulkIds(new Set());
      setSelectedId(null);
      await fetchList();
    } catch (err) {
      console.error('bulk_action_failed', err);
    }
  }

  // ---- Filtering + grouping ----
  const visibleSubmissions = useMemo(
    () => filterSubmissions(submissions, activeFilter, searchQuery),
    [submissions, activeFilter, searchQuery]
  );
  const weeks = useMemo(() => groupByWeek(visibleSubmissions), [visibleSubmissions]);

  // ---- Sign out ----
  async function handleSignOut() {
    if (!window.confirm('Sign out of the dashboard?')) return;
    await logout();
    navigate('/login', { replace: true });
  }

  // ---- Render ----
  return (
    <div className="h-[100dvh] overflow-hidden bg-cream text-burgundy font-hanken text-[14px] leading-[1.5] flex flex-col">
      {/* Top header */}
      <Header admin={admin} adminInitials={adminInitials} onSignOut={handleSignOut} />

      {/* Main layout */}
      <div className="flex-1 lg:grid lg:grid-cols-[minmax(360px,480px)_1fr] overflow-hidden">
        {/* LEFT: list */}
        <div
          className={
            'h-full bg-cream-soft border-r border-burgundy/[0.12] flex-col overflow-hidden ' +
            (selectedId ? 'hidden lg:flex' : 'flex')
          }
        >
          <ListHeader
            totalCount={counts.all}
            newCount={counts.new}
            counts={counts}
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />

          {selectedBulkIds.size > 0 && (
            <BulkToolbar
              count={selectedBulkIds.size}
              onClear={() => setSelectedBulkIds(new Set())}
              onAction={bulkAction}
            />
          )}

          {listLoading ? (
            <div className="flex-1 flex items-center justify-center text-burgundy-faint text-sm">
              Loading...
            </div>
          ) : listError ? (
            <div className="flex-1 flex items-center justify-center px-6 text-center">
              <p className="text-burgundy-soft text-sm">{listError}</p>
            </div>
          ) : visibleSubmissions.length === 0 ? (
            <EmptyState filter={activeFilter} hasSearch={!!searchQuery.trim()} />
          ) : viewMode === 'sheet' ? (
            <SheetView
              submissions={visibleSubmissions}
              selectedIds={selectedBulkIds}
              activeId={selectedId}
              onToggle={toggleBulk}
              onSelect={selectSubmission}
            />
          ) : (
            <div className="flex-1 overflow-y-auto iswis-scroll">
              {weeks.map((w) => (
                <div key={w.key}>
                  <WeekHeader label={w.label} count={w.items.length} isThisWeek={w.isThisWeek} />
                  {w.items.map((sub) => (
                    <ListItem
                      key={sub.id}
                      sub={sub}
                      selected={selectedId === sub.id}
                      isUnread={!readIds.has(sub.id)}
                      checked={selectedBulkIds.has(sub.id)}
                      onToggle={() => toggleBulk(sub.id)}
                      onClick={() => selectSubmission(sub.id)}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT: detail */}
        <div
          className={
            'h-full bg-cream-bright overflow-y-auto iswis-scroll flex-col ' +
            (selectedId ? 'flex' : 'hidden lg:flex')
          }
        >
          {selectedId == null ? (
            <DetailPlaceholder />
          ) : detailLoading && !detail ? (
            <div className="flex-1 flex items-center justify-center text-burgundy-faint text-sm">
              Loading dilemma...
            </div>
          ) : detailError && !detail ? (
            <div className="flex-1 flex items-center justify-center px-6 text-center">
              <p className="text-burgundy-soft text-sm">{detailError}</p>
            </div>
          ) : detail ? (
            <DetailPanel
              submission={detail}
              activity={detailActivity}
              comments={detailComments}
              replies={detailReplies}
              onBack={() => setSelectedId(null)}
              onStatusChange={(s) => patchSubmission({ status: s })}
              onStarToggle={() => patchSubmission({ starred: !detail.starred })}
              onCategoryChange={(category) => patchDetail({ category })}
              onSaveTranscript={(transcript) => patchDetail({ transcript })}
              onAddComment={addComment}
              onRetryTranscription={retryTranscription}
              onSendReply={sendReply}
              patching={patching}
              onPollTranscript={() => fetchDetail(detail.id, { silent: true })}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ---------------- Header ----------------

function Header({
  admin,
  adminInitials,
  onSignOut,
}: {
  admin: Admin | null;
  adminInitials: string;
  onSignOut: () => void;
}) {
  return (
    <div className="h-[56px] lg:h-[60px] bg-forest text-cream-bright flex items-center px-3 sm:px-5 lg:px-6 gap-3 lg:gap-8 flex-shrink-0">
      <div className="font-rye text-[14px] sm:text-[16px] lg:text-[18px] tracking-[0.5px] text-cream-bright whitespace-nowrap">
        <DotI>I</DotI> SAID WHAT <DotI>I</DotI> SAID
      </div>

      <div className="hidden sm:flex items-center gap-2 px-3.5 py-1.5 bg-forest-soft rounded-lg font-semibold text-[13px]">
        Main Dilemmas
      </div>

      <div className="hidden md:flex gap-1 ml-auto">
        <Link
          to="/admin"
          className="px-3.5 py-2 text-[13px] font-semibold rounded-md transition-all text-cream-bright bg-forest-soft"
        >
          Inbox
        </Link>
        <Link
          to="/admin/forms"
          className="px-3.5 py-2 text-[13px] font-semibold rounded-md transition-all text-cream-bright/70 hover:text-cream-bright"
        >
          Forms
        </Link>
        <Link
          to="/admin/activity"
          className="px-3.5 py-2 text-[13px] font-semibold rounded-md transition-all text-cream-bright/70 hover:text-cream-bright"
        >
          Activity
        </Link>
        <Link
          to="/admin/settings"
          className="px-3.5 py-2 text-[13px] font-semibold rounded-md transition-all text-cream-bright/70 hover:text-cream-bright"
        >
          Settings
        </Link>
      </div>

      <button
        type="button"
        onClick={onSignOut}
        title={`${admin?.email ?? ''} — click to sign out`}
        className="ml-auto md:ml-0 w-9 h-9 lg:w-8 lg:h-8 rounded-full bg-pink text-cream-bright font-bold text-[13px] flex items-center justify-center cursor-pointer border-2 border-cream-bright hover:opacity-90"
      >
        {adminInitials}
      </button>
    </div>
  );
}

// ---------------- List header (search, filter chips) ----------------

function ListHeader({
  totalCount,
  newCount,
  counts,
  activeFilter,
  onFilterChange,
  searchQuery,
  onSearchChange,
  viewMode,
  onViewModeChange,
}: {
  totalCount: number;
  newCount: number;
  counts: Counts;
  activeFilter: FilterKey;
  onFilterChange: (f: FilterKey) => void;
  searchQuery: string;
  onSearchChange: (s: string) => void;
  viewMode: 'list' | 'sheet';
  onViewModeChange: (mode: 'list' | 'sheet') => void;
}) {
  const chips: { key: FilterKey; label: string; count?: number; countColor?: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'new', label: 'New', count: counts.new, countColor: 'text-pink-deep' },
    { key: 'shortlisted', label: 'Shortlisted', count: counts.shortlisted, countColor: 'text-orange' },
    { key: 'voicenotes', label: 'Voicenotes', count: counts.voicenotes },
    { key: 'transcript_failed', label: 'Needs transcript', count: counts.transcript_failed, countColor: 'text-danger' },
    { key: 'starred', label: 'Starred', count: counts.starred },
    { key: 'archived', label: 'Archived', count: counts.archived },
  ];
  return (
    <div className="px-5 pt-[18px] pb-3.5 border-b border-burgundy/[0.12] flex-shrink-0">
      <div className="flex items-baseline gap-3 mb-3.5">
        <div className="font-bagel text-[22px] text-burgundy tracking-[0.3px]">Inbox</div>
        <div className="text-[12px] text-burgundy-faint font-semibold">
          {totalCount} · {newCount} new
        </div>
      </div>
      <input
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Search dilemmas..."
        className="search-icon-bg w-full pl-9 pr-3 py-[9px] bg-cream-bright border border-burgundy/[0.12] rounded-lg font-hanken text-[13px] text-burgundy placeholder:text-burgundy-faint focus:outline-none focus:border-pink focus:shadow-[0_0_0_3px_rgba(238,58,140,0.08)] transition-[border-color,box-shadow]"
      />
      <div className="flex gap-1.5 flex-wrap mt-3">
        {chips.map((c) => (
          <button
            key={c.key}
            onClick={() => onFilterChange(c.key)}
            className={
              'px-2.5 py-[5px] rounded-full text-[12px] font-semibold cursor-pointer flex items-center gap-1 transition-all border ' +
              (activeFilter === c.key
                ? 'bg-burgundy text-cream-bright border-burgundy'
                : 'bg-cream-bright text-burgundy-soft border-burgundy/[0.12] hover:border-burgundy')
            }
          >
            {c.label}
            {c.count != null && c.count > 0 && (
              <span className={`font-bold ${c.countColor ?? ''}`}>{c.count}</span>
            )}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onViewModeChange(viewMode === 'list' ? 'sheet' : 'list')}
          className="px-2.5 py-[5px] rounded-full text-[12px] font-semibold cursor-pointer flex items-center gap-1 transition-all border bg-cream-bright text-burgundy-soft border-burgundy/[0.12] hover:border-burgundy"
        >
          {viewMode === 'list' ? 'Sheet' : 'List'}
        </button>
      </div>
    </div>
  );
}

function BulkToolbar({
  count,
  onClear,
  onAction,
}: {
  count: number;
  onClear: () => void;
  onAction: (action: Status | 'delete') => void;
}) {
  return (
    <div className="px-4 py-2.5 bg-burgundy text-cream-bright flex items-center gap-2 flex-wrap">
      <span className="font-bold text-[12px]">{count} selected</span>
      {(['shortlisted', 'used', 'archived'] as Status[]).map((status) => (
        <button
          key={status}
          type="button"
          onClick={() => onAction(status)}
          className="px-2.5 py-1.5 rounded-md bg-cream-bright text-burgundy text-[12px] font-bold"
        >
          {capitalize(status)}
        </button>
      ))}
      <button
        type="button"
        onClick={() => onAction('delete')}
        className="px-2.5 py-1.5 rounded-md bg-danger text-cream-bright text-[12px] font-bold"
      >
        Delete
      </button>
      <button type="button" onClick={onClear} className="ml-auto text-[12px] font-bold">
        Clear
      </button>
    </div>
  );
}

// ---------------- Empty state (brief §11) ----------------

function EmptyState({ filter, hasSearch }: { filter: FilterKey; hasSearch: boolean }) {
  let msg = '';
  if (hasSearch) msg = "Nothing matches that search. Try a different word.";
  else if (filter === 'new') msg = "No new ones. The babes will spill soon.";
  else if (filter === 'shortlisted') msg = "Nothing shortlisted yet. Pick a few from New.";
  else if (filter === 'voicenotes') msg = "No voicenotes yet. Voicenote saving launches soon.";
  else if (filter === 'transcript_failed') msg = "No failed transcripts. Lovely.";
  else if (filter === 'starred') msg = "No stars yet. Tap the star on a dilemma to bookmark it.";
  else if (filter === 'archived') msg = "Nothing archived.";
  else msg = "Nothing new yet. The babes will spill soon.";

  return (
    <div className="flex-1 flex items-center justify-center px-8 text-center">
      <p className="text-burgundy-soft text-[14px] max-w-[300px] leading-relaxed">{msg}</p>
    </div>
  );
}

// ---------------- Week header ----------------

function WeekHeader({
  label,
  count,
  isThisWeek,
}: {
  label: string;
  count: number;
  isThisWeek: boolean;
}) {
  return (
    <div
      className={
        'flex items-center justify-between px-5 pt-3.5 pb-2 sticky top-0 z-[5] border-b border-burgundy/[0.12] select-none ' +
        (isThisWeek
          ? 'bg-gradient-to-r from-pink/[0.06] to-transparent'
          : 'bg-cream-soft')
      }
    >
      <div className="font-bagel text-[14px] tracking-[1px] uppercase text-burgundy">
        {isThisWeek && <span className="text-pink-deep">●</span>} {label}{' '}
        <span className="text-[11.5px] text-burgundy-faint font-semibold tracking-[0.3px] normal-case">
          · {count} {count === 1 ? 'dilemma' : 'dilemmas'}
        </span>
      </div>
    </div>
  );
}

// ---------------- List item ----------------

function ListItem({
  sub,
  selected,
  isUnread,
  checked,
  onToggle,
  onClick,
}: {
  sub: Submission;
  selected: boolean;
  isUnread: boolean;
  checked: boolean;
  onToggle: () => void;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className={
        'relative px-4 sm:px-5 py-4 lg:py-3.5 border-b border-burgundy/[0.12] cursor-pointer flex gap-3 transition-colors ' +
        (selected ? 'bg-cream-bright' : 'hover:bg-cream-bright')
      }
    >
      {selected && (
        <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-pink" aria-hidden="true" />
      )}
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        onClick={(e) => e.stopPropagation()}
        className="mt-1 w-4 h-4 flex-shrink-0"
        aria-label={`Select ${sub.submitter_name}`}
      />
      <StatusDot status={sub.status} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div
            className={
              'text-[13.5px] text-burgundy whitespace-nowrap overflow-hidden text-ellipsis flex-1 ' +
              (isUnread ? 'font-bold' : 'font-semibold')
            }
          >
            {sub.submitter_name}
          </div>
          <div className="text-[11.5px] text-burgundy-faint font-medium flex-shrink-0">
            {formatRelative(sub.submitted_at)}
          </div>
        </div>
        <div className="text-[13px] text-burgundy-soft leading-[1.45] mb-1.5 overflow-hidden line-clamp-2">
          {sub.is_voicenote && (
            <span className="text-burgundy-faint italic">
              [Voicenote · {formatDuration(sub.voicenote_duration_seconds)}]{' '}
            </span>
          )}
          {sub.text_snippet || (sub.is_voicenote ? 'Transcription pending.' : '')}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {sub.category && (
            <Tag category={sub.category}>{capitalize(sub.category)}</Tag>
          )}
          <span className="inline-flex items-center gap-[3px] text-[11px] text-burgundy-faint font-semibold">
            {sub.is_voicenote ? <MicIcon /> : <WordsIcon />}
            {sub.is_voicenote
              ? formatDuration(sub.voicenote_duration_seconds)
              : `${sub.word_count} words`}
          </span>
          {sub.starred && (
            <span className="inline-flex items-center gap-[3px] text-[11px] text-orange font-semibold">
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-[11px] h-[11px]">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </span>
          )}
          {sub.comment_count > 0 && (
            <span className="inline-flex items-center gap-[3px] text-[11px] text-burgundy-faint font-semibold">
              Notes {sub.comment_count}
            </span>
          )}
          {sub.is_voicenote && sub.transcription_status === 'failed' && (
            <span className="inline-flex items-center gap-[3px] text-[11px] text-danger font-bold">
              Transcript failed
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function SheetView({
  submissions,
  selectedIds,
  activeId,
  onToggle,
  onSelect,
}: {
  submissions: Submission[];
  selectedIds: Set<string>;
  activeId: string | null;
  onToggle: (id: string) => void;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="flex-1 overflow-auto iswis-scroll">
      <table className="min-w-[720px] w-full text-left text-[13px]">
        <thead className="sticky top-0 bg-cream-deep text-burgundy z-[4]">
          <tr>
            <th className="w-10 px-3 py-2"></th>
            <th className="px-3 py-2">Name</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Category</th>
            <th className="px-3 py-2">Length</th>
            <th className="px-3 py-2">Submitted</th>
          </tr>
        </thead>
        <tbody>
          {submissions.map((sub) => (
            <tr
              key={sub.id}
              onClick={() => onSelect(sub.id)}
              className={
                'border-b border-burgundy/[0.10] cursor-pointer ' +
                (activeId === sub.id ? 'bg-cream-bright' : 'hover:bg-cream-bright')
              }
            >
              <td className="px-3 py-3">
                <input
                  type="checkbox"
                  checked={selectedIds.has(sub.id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => onToggle(sub.id)}
                  aria-label={`Select ${sub.submitter_name}`}
                />
              </td>
              <td className="px-3 py-3 font-bold text-burgundy max-w-[180px] truncate">
                {sub.submitter_name}
              </td>
              <td className="px-3 py-3">{capitalize(sub.status)}</td>
              <td className="px-3 py-3">{sub.category ? capitalize(sub.category) : 'Other'}</td>
              <td className="px-3 py-3">
                {sub.is_voicenote ? formatDuration(sub.voicenote_duration_seconds) : `${sub.word_count} words`}
              </td>
              <td className="px-3 py-3 text-burgundy-faint">{formatRelative(sub.submitted_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusDot({ status }: { status: Status }) {
  const map: Record<Status, string> = {
    new: 'bg-pink shadow-[0_0_0_3px_rgba(238,58,140,0.2)]',
    shortlisted: 'bg-orange',
    used: 'bg-teal',
    archived: 'bg-burgundy-faint opacity-50',
  };
  return (
    <span
      className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${map[status]}`}
      aria-hidden="true"
    />
  );
}

function Tag({ category, children }: { category: Category; children: ReactNode }) {
  const styles: Record<Category, string> = {
    relationships: 'bg-pink/[0.12] text-pink-deep',
    family: 'bg-orange/[0.15] text-[#B86A1E]',
    money: 'bg-teal/[0.15] text-[#1F8A75]',
    work: 'bg-purple/[0.15] text-[#6B4592]',
    friendship: 'bg-pink-light/30 text-[#B05080]',
    other: 'bg-burgundy/[0.08] text-burgundy-soft',
  };
  return (
    <span
      className={`text-[10.5px] font-bold tracking-[0.5px] uppercase px-2 py-0.5 rounded ${styles[category]}`}
    >
      {children}
    </span>
  );
}

// ---------------- Detail panel ----------------

function DetailPlaceholder() {
  return (
    <div className="flex-1 flex items-center justify-center px-8 text-center">
      <p className="text-burgundy-soft text-[14px] max-w-[280px]">
        Pick a dilemma on the left to read it.
      </p>
    </div>
  );
}

function DetailPanel({
  submission,
  activity,
  comments,
  replies,
  onBack,
  onStatusChange,
  onStarToggle,
  onCategoryChange,
  onSaveTranscript,
  onAddComment,
  onRetryTranscription,
  onSendReply,
  patching,
  onPollTranscript,
}: {
  submission: SubmissionFull;
  activity: ActivityEntry[];
  comments: CommentEntry[];
  replies: ReplyEntry[];
  onBack: () => void;
  onStatusChange: (s: Status) => void;
  onStarToggle: () => void;
  onCategoryChange: (category: Category) => Promise<void>;
  onSaveTranscript: (transcript: string) => Promise<void>;
  onAddComment: (body: string) => Promise<void>;
  onRetryTranscription: () => Promise<void>;
  onSendReply: (body: string) => Promise<void>;
  patching: boolean;
  onPollTranscript: () => void;
}) {
  const [editingTranscript, setEditingTranscript] = useState(false);
  const [transcriptDraft, setTranscriptDraft] = useState(submission.transcript ?? '');

  // For voicenotes the transcript may still be processing in the background
  // (Whisper kicked off via ctx.waitUntil on submission). Poll every 6s
  // until it lands. The fetch is silent so the panel doesn't flash.
  useEffect(() => {
    if (!submission.is_voicenote) return;
    if (submission.transcript) return; // already have it
    if (submission.transcription_status === 'failed') return;
    const interval = window.setInterval(() => {
      onPollTranscript();
    }, 6000);
    return () => window.clearInterval(interval);
  }, [
    submission.is_voicenote,
    submission.transcript,
    submission.transcription_status,
    submission.id,
    onPollTranscript,
  ]);

  const transcriptText = submission.transcript ?? '';
  const dilemmaText = submission.is_voicenote ? transcriptText : (submission.text_content ?? '');
  // For rich-text-edited dilemmas, text_content contains HTML. We render via
  // dangerouslySetInnerHTML, but only when it actually looks like HTML and only
  // for text mode. Voicenote transcripts are always plain text.
  const isHtmlContent =
    !submission.is_voicenote &&
    !!submission.text_content &&
    /<[a-z][^>]*>/i.test(submission.text_content);

  useEffect(() => {
    setTranscriptDraft(submission.transcript ?? '');
    setEditingTranscript(false);
  }, [submission.id, submission.transcript]);

  return (
    <>
      <div className="px-4 sm:px-6 lg:px-8 pt-4 lg:pt-6 pb-4 lg:pb-5 border-b border-burgundy/[0.12] sticky top-0 bg-cream-bright z-10">
        <button
          type="button"
          onClick={onBack}
          className="lg:hidden mb-3 inline-flex items-center gap-2 text-[13px] font-bold text-pink-deep"
        >
          <span aria-hidden="true">←</span>
          Inbox
        </button>
        <div className="flex items-center justify-between gap-3 lg:gap-4 mb-3.5">
          <div className="flex-1 min-w-0">
            <div className="font-bagel text-[24px] text-burgundy tracking-[0.3px] mb-0.5 break-words">
              {submission.submitter_name}
            </div>
            <div className="text-[12.5px] text-burgundy-faint font-medium">
              Submitted {formatRelative(submission.submitted_at)}
              {submission.submitter_email && (
                <>
                  {' · '}
                  <span className="text-pink-deep">{submission.submitter_email}</span>
                </>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              title="Download prep note"
              onClick={() => downloadPrepNote(submission, comments)}
              className="w-11 h-11 lg:w-[34px] lg:h-[34px] rounded-lg cursor-pointer flex items-center justify-center transition-all border bg-transparent text-burgundy-soft border-burgundy/[0.12] hover:bg-cream hover:text-burgundy hover:border-burgundy"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <path d="M7 10l5 5 5-5" />
                <path d="M12 15V3" />
              </svg>
            </button>
            <button
              title="Star"
              onClick={onStarToggle}
              disabled={patching}
              className={
                'w-11 h-11 lg:w-[34px] lg:h-[34px] rounded-lg cursor-pointer flex items-center justify-center transition-all border disabled:opacity-50 disabled:cursor-not-allowed ' +
                (submission.starred
                  ? 'text-orange border-orange bg-orange/[0.08]'
                  : 'bg-transparent text-burgundy-soft border-burgundy/[0.12] hover:bg-cream hover:text-burgundy hover:border-burgundy')
              }
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex gap-1 bg-cream p-1 rounded-[10px] mt-1">
          {STATUS_VALUES.map((s) => (
            <button
              key={s}
              onClick={() => onStatusChange(s)}
              disabled={patching || submission.status === s}
              className={
                'flex-1 px-3 py-[7px] bg-transparent border-none cursor-pointer font-hanken text-[12px] font-bold tracking-[0.3px] rounded-[7px] transition-all uppercase disabled:cursor-default ' +
                (submission.status === s
                  ? statusActiveClass(s)
                  : 'text-burgundy-faint hover:text-burgundy')
              }
            >
              {capitalize(s)}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 pt-5 lg:pt-6 pb-28 lg:pb-10 flex-1">
        <div className="flex gap-1.5 mb-6 flex-wrap items-center">
          <label className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.5px] uppercase text-burgundy-faint">
            Category
            <select
              value={submission.category ?? ''}
              onChange={(e) => {
                if (e.target.value) onCategoryChange(e.target.value as Category);
              }}
              disabled={patching}
              className="min-h-9 rounded-lg border border-burgundy/[0.16] bg-cream px-2 py-1 text-[12px] font-bold text-burgundy normal-case tracking-normal focus:outline-none focus:border-pink"
            >
              <option value="">Pick one</option>
              {CATEGORY_VALUES.map((category) => (
                <option key={category} value={category}>
                  {capitalize(category)}
                </option>
              ))}
            </select>
          </label>
          {submission.category && <Tag category={submission.category}>{capitalize(submission.category)}</Tag>}
          <span className="text-[10.5px] font-bold tracking-[0.5px] uppercase px-2 py-0.5 rounded bg-cream text-burgundy-soft">
            {submission.is_voicenote
              ? formatDuration(submission.voicenote_duration_seconds)
              : `${submission.word_count} words`}
          </span>
        </div>

        {submission.is_voicenote && (
          <Section label="Voicenote">
            <VoicenotePlayer submissionId={submission.id} />
          </Section>
        )}

        <Section label={submission.is_voicenote ? 'Transcript' : 'The Dilemma'}>
          {submission.is_voicenote && !transcriptText && submission.transcription_status === 'failed' ? (
            <div className="rounded-xl border border-danger/25 bg-danger/[0.06] p-4">
              <div className="text-[14px] text-danger font-bold mb-1">
                Transcription failed.
              </div>
              <div className="text-[13px] text-burgundy-soft mb-3">
                {submission.transcription_error ||
                  'The audio is saved, but Whisper did not return a transcript.'}
              </div>
              <button
                type="button"
                onClick={onRetryTranscription}
                className="min-h-11 px-4 py-2 rounded-lg bg-pink text-cream-bright font-bold text-[13px] hover:bg-pink-deep"
              >
                Try transcription again
              </button>
            </div>
          ) : submission.is_voicenote && !transcriptText ? (
            <div className="text-[14px] text-burgundy-faint italic flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-pink animate-pulse" aria-hidden="true" />
              {submission.transcription_status === 'pending'
                ? 'Queued for transcription...'
                : 'Transcribing... this usually takes 30 seconds to a couple of minutes.'}
            </div>
          ) : submission.is_voicenote && editingTranscript ? (
            <div className="space-y-3">
              <textarea
                value={transcriptDraft}
                onChange={(e) => setTranscriptDraft(e.target.value)}
                rows={8}
                className="w-full rounded-xl border border-burgundy/[0.18] bg-cream px-3.5 py-3 text-[14px] text-burgundy focus:outline-none focus:border-pink focus:shadow-[0_0_0_3px_rgba(238,58,140,0.10)]"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    await onSaveTranscript(transcriptDraft);
                    setEditingTranscript(false);
                  }}
                  className="min-h-11 px-4 py-2 rounded-lg bg-burgundy text-cream-bright font-bold text-[13px]"
                >
                  Save transcript
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setTranscriptDraft(submission.transcript ?? '');
                    setEditingTranscript(false);
                  }}
                  className="min-h-11 px-4 py-2 rounded-lg border border-burgundy/[0.16] text-burgundy font-bold text-[13px]"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : isHtmlContent ? (
            <div
              className="text-[15.5px] leading-[1.65] text-burgundy break-words [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_strong]:font-bold [&_em]:italic [&_u]:underline [&_a]:text-pink-deep [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: sanitizeRichText(submission.text_content ?? '') }}
            />
          ) : (
            <>
              <div className="text-[15.5px] leading-[1.65] text-burgundy whitespace-pre-wrap break-words">
                {dilemmaText}
              </div>
              {submission.is_voicenote && transcriptText && (
                <button
                  type="button"
                  onClick={() => setEditingTranscript(true)}
                  className="mt-3 min-h-10 px-3 py-2 rounded-lg border border-burgundy/[0.16] text-burgundy-soft hover:text-burgundy text-[13px] font-bold"
                >
                  Edit transcript
                </button>
              )}
            </>
          )}
        </Section>

        <Section label="Notes (between us)">
          <CommentsPanel comments={comments} onAddComment={onAddComment} />
        </Section>

        <Section label="Activity">
          {activity.length === 0 ? (
            <div className="text-[13px] text-burgundy-faint">No activity yet.</div>
          ) : (
            <div className="flex flex-col gap-2">
              {activity.map((a) => (
                <div
                  key={a.id}
                  className="flex gap-2 items-center text-[12px] text-burgundy-faint"
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full bg-burgundy-faint flex-shrink-0"
                    aria-hidden="true"
                  />
                  <div>
                    {describeActivity(a)} · {formatRelative(a.created_at)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section label="Replies">
          <RepliesPanel
            replies={replies}
            canReply={!!submission.submitter_email}
            onSendReply={onSendReply}
          />
        </Section>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 py-3 lg:py-4 border-t border-burgundy/[0.12] bg-cream-soft flex gap-2 sticky bottom-0">
        <span className="text-[11px] text-burgundy-faint self-center">
          Tip: use Notes for the team, Replies for the submitter.
        </span>
      </div>
    </>
  );
}

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mb-8">
      <div className="text-[11px] font-bold tracking-[1.5px] uppercase text-burgundy-faint mb-2.5">
        {label}
      </div>
      {children}
    </div>
  );
}

function CommentsPanel({
  comments,
  onAddComment,
}: {
  comments: CommentEntry[];
  onAddComment: (body: string) => Promise<void>;
}) {
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    const trimmed = body.trim();
    if (!trimmed) {
      setError('Add a note first.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await onAddComment(trimmed);
      setBody('');
    } catch (err) {
      console.error('comment_submit_failed', err);
      setError("Couldn't save that note. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-3">
      {comments.length === 0 ? (
        <div className="text-[13px] text-burgundy-faint italic">
          No notes yet. Leave one for the other babe.
        </div>
      ) : (
        <div className="space-y-2">
          {comments.map((comment) => (
            <div
              key={comment.id}
              className="rounded-xl border border-burgundy/[0.12] bg-cream-soft px-3.5 py-3"
            >
              <div className="flex items-center justify-between gap-3 mb-1">
                <div className="text-[12px] font-bold text-burgundy">
                  {comment.admin_name || comment.admin_email || 'Admin'}
                </div>
                <div className="text-[11px] text-burgundy-faint flex-shrink-0">
                  {formatRelative(comment.created_at)}
                </div>
              </div>
              <div className="text-[13.5px] leading-relaxed text-burgundy whitespace-pre-wrap break-words">
                {comment.body}
              </div>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-2">
        <textarea
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
            if (error) setError('');
          }}
          placeholder="Add a note for the team..."
          rows={3}
          className="w-full rounded-xl border border-burgundy/[0.18] bg-cream px-3.5 py-3 text-[14px] text-burgundy placeholder:text-burgundy-faint focus:outline-none focus:border-pink focus:shadow-[0_0_0_3px_rgba(238,58,140,0.10)] resize-none"
        />
        {error && <div className="text-[12px] font-semibold text-danger">{error}</div>}
        <button
          type="submit"
          disabled={submitting || !body.trim()}
          className="min-h-11 px-4 py-2 rounded-lg bg-burgundy text-cream-bright font-bold text-[13px] disabled:opacity-45 disabled:cursor-not-allowed hover:bg-burgundy-soft"
        >
          {submitting ? 'Saving...' : 'Save note'}
        </button>
      </form>
    </div>
  );
}

function RepliesPanel({
  replies,
  canReply,
  onSendReply,
}: {
  replies: ReplyEntry[];
  canReply: boolean;
  onSendReply: (body: string) => Promise<void>;
}) {
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canReply || submitting) return;
    const trimmed = body.trim();
    if (!trimmed) {
      setError('Write the reply first.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await onSendReply(trimmed);
      setBody('');
    } catch (err) {
      console.error('reply_submit_failed', err);
      setError("Couldn't send that reply. Check Resend and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-3">
      {!canReply && (
        <div className="rounded-xl border border-burgundy/[0.12] bg-cream-soft px-3.5 py-3 text-[13px] text-burgundy-faint">
          This dilemma has no email address, so replies are hidden.
        </div>
      )}

      {replies.length > 0 && (
        <div className="space-y-2">
          {replies.map((reply) => (
            <div
              key={reply.id}
              className="rounded-xl border border-burgundy/[0.12] bg-cream-soft px-3.5 py-3"
            >
              <div className="flex items-center justify-between gap-3 mb-1">
                <div className="text-[12px] font-bold text-burgundy">
                  {reply.direction === 'out'
                    ? reply.admin_name || reply.admin_email || 'Admin'
                    : 'Submitter'}
                </div>
                <div className="text-[11px] text-burgundy-faint flex-shrink-0">
                  {formatRelative(reply.sent_at)}
                </div>
              </div>
              <div className="text-[13.5px] leading-relaxed text-burgundy whitespace-pre-wrap break-words">
                {reply.body}
              </div>
            </div>
          ))}
        </div>
      )}

      {canReply && (
        <form onSubmit={handleSubmit} className="space-y-2">
          <textarea
            value={body}
            onChange={(e) => {
              setBody(e.target.value);
              if (error) setError('');
            }}
            placeholder="Reply to submitter..."
            rows={4}
            className="w-full rounded-xl border border-burgundy/[0.18] bg-cream px-3.5 py-3 text-[14px] text-burgundy placeholder:text-burgundy-faint focus:outline-none focus:border-pink focus:shadow-[0_0_0_3px_rgba(238,58,140,0.10)] resize-none"
          />
          {error && <div className="text-[12px] font-semibold text-danger">{error}</div>}
          <button
            type="submit"
            disabled={submitting || !body.trim()}
            className="min-h-11 px-4 py-2 rounded-lg bg-pink text-cream-bright font-bold text-[13px] disabled:opacity-45 disabled:cursor-not-allowed hover:bg-pink-deep"
          >
            {submitting ? 'Sending...' : 'Send reply'}
          </button>
        </form>
      )}
    </div>
  );
}

// ---------------- Icons + small ----------------

function DotI({ children }: { children: ReactNode }) {
  return (
    <span className="relative inline-block">
      <span
        className="absolute left-1/2 -translate-x-1/2 w-1 h-1 bg-pink rounded-full"
        style={{ top: '-6px' }}
        aria-hidden="true"
      />
      {children}
    </span>
  );
}

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-[11px] h-[11px]">
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <path d="M12 19v3" />
    </svg>
  );
}

function WordsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-[11px] h-[11px]">
      <path d="M14 4h6v6" />
      <path d="M10 20H4v-6" />
      <path d="M20 4l-7 7" />
      <path d="M4 20l7-7" />
    </svg>
  );
}

// ---------------- Helpers ----------------

// ---- Voicenote player ----
// Fetches the audio object as a Blob (so the session cookie is sent) and feeds
// it to a native <audio controls> element via an object URL.
function VoicenotePlayer({ submissionId }: { submissionId: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    let revoke: string | null = null;
    setSrc(null);
    setError('');
    fetch(`${API_BASE}/api/submissions/${submissionId}/audio`, {
      credentials: 'include',
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        revoke = url;
        setSrc(url);
      })
      .catch((err) => {
        if (!cancelled) {
          setError("We couldn't load the audio. Try refreshing.");
          console.error('audio_fetch_failed', err);
        }
      });
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [submissionId]);

  if (error) {
    return (
      <div className="text-[13px] text-burgundy-faint italic">{error}</div>
    );
  }
  if (!src) {
    return (
      <div className="text-[13px] text-burgundy-faint italic">Loading audio...</div>
    );
  }
  return (
    <audio
      controls
      src={src}
      className="w-full h-10 rounded-lg"
      preload="metadata"
    />
  );
}

// ---- Rich-text sanitizer ----
// Submitter form's dilemma input uses a contentEditable rich-text editor and
// posts raw HTML to the Worker. The admin detail panel renders that HTML, so
// we strip the obvious XSS vectors here before insertion.
//
// This is intentionally conservative: strip script tags, all on* event
// handlers, and javascript:/data: URLs in href/src. Not a full DOMPurify, but
// enough for trusted-ish content (the form is rate-limited and Turnstile-gated;
// the only people who can write HTML here are the same people FK and Jollz
// are reading dilemmas from).
function sanitizeRichText(html: string): string {
  let out = html;
  // Strip <script>...</script> and <style>...</style>
  out = out.replace(/<\s*(script|style)[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, '');
  // Strip on* event handler attributes (e.g. onclick="...", onerror='...').
  out = out.replace(/\son[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '');
  // Neutralise javascript:/data: URLs in href and src.
  out = out.replace(
    /\s(href|src)\s*=\s*(["'])\s*(?:javascript|data|vbscript):[^"']*\2/gi,
    ' $1=$2#$2'
  );
  return out;
}

function adminInitialsFrom(name?: string | null, email?: string | null): string {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  }
  if (email) return email.slice(0, 2).toUpperCase();
  return '??';
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function statusActiveClass(s: Status): string {
  const map: Record<Status, string> = {
    new: 'bg-pink text-cream-bright',
    shortlisted: 'bg-orange text-cream-bright',
    used: 'bg-teal text-cream-bright',
    archived: 'bg-burgundy-faint text-cream-bright',
  };
  return map[s];
}

function downloadPrepNote(submission: SubmissionFull, comments: CommentEntry[]) {
  const title = `iswis-${submission.submitter_name || 'dilemma'}-${submission.id.slice(0, 8)}.txt`
    .replace(/[^\w.-]+/g, '-')
    .toLowerCase();
  const body = [
    'ISWIS DILEMMA PREP',
    '',
    `Submitter: ${submission.submitter_name}`,
    `Submitted: ${new Date(submission.submitted_at).toLocaleString()}`,
    `Status: ${capitalize(submission.status)}`,
    `Category: ${submission.category ? capitalize(submission.category) : 'Uncategorised'}`,
    `Format: ${submission.is_voicenote ? 'Voicenote' : 'Text'}`,
    '',
    'DILEMMA',
    stripHtmlForExport(submission.text_content || submission.transcript || ''),
    '',
    'NOTES',
    comments.length
      ? comments
          .map(
            (c) =>
              `${c.admin_name || c.admin_email || 'Admin'} (${new Date(c.created_at).toLocaleString()}):\n${c.body}`
          )
          .join('\n\n')
      : 'No notes yet.',
  ].join('\n');
  const blob = new Blob([body], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = title;
  a.click();
  URL.revokeObjectURL(url);
}

function stripHtmlForExport(s: string): string {
  const div = document.createElement('div');
  div.innerHTML = s;
  return (div.textContent || div.innerText || s).trim();
}

function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return '—';
  const m = Math.floor(seconds / 60);
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function formatRelative(iso: string): string {
  const now = Date.now();
  const t = new Date(iso).getTime();
  const diffMs = now - t;
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHr = Math.floor(diffMs / 3_600_000);
  const diffDay = Math.floor(diffMs / 86_400_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  const d = new Date(iso);
  // e.g. "May 8"
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function describeActivity(a: ActivityEntry): string {
  const who = a.admin_name || a.admin_email || 'Someone';
  switch (a.action) {
    case 'status_changed': {
      const d = a.details as { from?: string; to?: string } | null;
      const to = d?.to ? capitalize(d.to) : 'a new status';
      return `${who} moved this to ${to}`;
    }
    case 'starred':
      return `${who} starred this`;
    case 'unstarred':
      return `${who} unstarred this`;
    case 'commented':
      return `${who} commented`;
    case 'replied':
      return `${who} replied`;
    default:
      return `${who} ${a.action.replace(/_/g, ' ')}`;
  }
}

// ---- localStorage read-state ----

function readStateKey(adminId: string | undefined): string | null {
  if (!adminId) return null;
  return `iswis_read_${adminId}`;
}

function loadReadIds(adminId: string | undefined): Set<string> {
  const key = readStateKey(adminId);
  if (!key) return new Set();
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function saveReadIds(adminId: string, ids: Set<string>) {
  const key = readStateKey(adminId);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(Array.from(ids)));
  } catch {
    // Quota exceeded or storage disabled — non-fatal.
  }
}

// ---- Filtering + week grouping ----

function filterSubmissions(
  list: Submission[],
  filter: FilterKey,
  search: string
): Submission[] {
  const s = search.trim().toLowerCase();
  return list.filter((sub) => {
    if (filter === 'new' && sub.status !== 'new') return false;
    if (filter === 'shortlisted' && sub.status !== 'shortlisted') return false;
    if (filter === 'archived' && sub.status !== 'archived') return false;
    if (filter === 'starred' && !sub.starred) return false;
    if (filter === 'voicenotes' && !sub.is_voicenote) return false;
    if (filter === 'transcript_failed' && sub.transcription_status !== 'failed') return false;
    if (s) {
      const haystack = `${sub.submitter_name} ${sub.text_snippet} ${sub.submitter_email ?? ''}`.toLowerCase();
      if (!haystack.includes(s)) return false;
    }
    return true;
  });
}

type WeekGroup = {
  key: string;
  label: string;
  isThisWeek: boolean;
  items: Submission[];
};

function groupByWeek(list: Submission[]): WeekGroup[] {
  // Buckets keyed by start-of-week ISO date string (Monday-based).
  const buckets = new Map<string, WeekGroup>();
  const now = new Date();
  const thisWeekStart = startOfWeek(now).toISOString().slice(0, 10);

  for (const sub of list) {
    const d = new Date(sub.submitted_at);
    const wkStart = startOfWeek(d);
    const key = wkStart.toISOString().slice(0, 10);
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = {
        key,
        label: labelForWeek(wkStart, thisWeekStart === key),
        isThisWeek: thisWeekStart === key,
        items: [],
      };
      buckets.set(key, bucket);
    }
    bucket.items.push(sub);
  }
  // Sort buckets newest-first.
  return Array.from(buckets.values()).sort((a, b) => (a.key < b.key ? 1 : -1));
}

function startOfWeek(d: Date): Date {
  // Monday-based week (per the mockup's "Last week (May 4-10)" range).
  const out = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const day = out.getUTCDay(); // 0 Sun ... 6 Sat
  const shift = day === 0 ? -6 : 1 - day; // shift to Monday
  out.setUTCDate(out.getUTCDate() + shift);
  return out;
}

function labelForWeek(start: Date, isThisWeek: boolean): string {
  if (isThisWeek) return 'This week';
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);
  // e.g. "May 4 - 10" or "Apr 27 - May 3"
  const sameMonth = start.getUTCMonth() === end.getUTCMonth();
  const fmt = (d: Date, withMonth: boolean) =>
    withMonth
      ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
      : d.getUTCDate().toString();
  return `${fmt(start, true)} - ${sameMonth ? fmt(end, false) : fmt(end, true)}`;
}
